import React from 'react';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import {
  ReportBody,
  ReportField,
  ReportFooter,
  ReportHeader,
  ReportMeta,
  ReportMetaRow,
  ReportNote,
  ReportPack,
  ReportPage,
  ReportSignLine,
  ReportSigns,
  ReportTitleRow,
} from '../../../components/ui/Report';
import type { CrewVesselListResponse, CrewVesselListRow } from '../types';

function dash(v: React.ReactNode) {
  if (v == null || v === '') return '—';
  return v;
}

function fmtDate(v?: string | null) {
  return v ? formatDateGlobal(v) : '—';
}

function fullName(row: CrewVesselListRow): string {
  return [row.lastName, row.firstName, row.middleName].filter(Boolean).join(' ').trim() || '—';
}

function statusLabel(status?: string | null): string {
  if (!status) return '—';
  if (status === 'ONB') return 'On board';
  if (status === 'S/OFF' || status === 'SOFF') return 'Signed off';
  return status;
}

export type OnboardCrewListReportProps = {
  data: CrewVesselListResponse;
  companyLogoSrc?: string | null;
  companyName?: string | null;
};

export const OnboardCrewListReport: React.FC<OnboardCrewListReportProps> = ({
  data,
  companyLogoSrc = null,
  companyName = null,
}) => {
  const vessel = data.vessel;
  const vesselName = vessel?.vesselName || '—';
  const flag = vessel?.flag?.name || vessel?.flag?.isoCode2 || '—';
  const periodLabel =
    data.periodFrom && data.periodTo && data.periodFrom !== data.periodTo
      ? `${fmtDate(data.periodFrom)} – ${fmtDate(data.periodTo)}`
      : fmtDate(data.periodFrom || data.periodTo);
  const rows = data.rows ?? [];
  const safeManning = vessel?.safeManning;

  return (
    <ReportPack className="crew-list-print-report crew-list-print-onboard">
      <ReportPage size="portrait">
        <ReportHeader>
          <ReportTitleRow
            title="Onboard Crew List"
            formId="Vessel manning"
            logoSrc={companyLogoSrc}
            logoAlt={companyName || 'Management company logo'}
          />
          <ReportMeta>
            <ReportMetaRow>
              <ReportField label="Vessel" value={vesselName} />
              <ReportField label="IMO number" value={dash(vessel?.imoNumber)} />
              <ReportField label="Call sign" value={dash(vessel?.callSign)} />
            </ReportMetaRow>
            <ReportMetaRow>
              <ReportField label="Flag" value={flag} />
              <ReportField label="Period" value={periodLabel} />
              <ReportField
                label="Crew / safe manning"
                value={`${rows.length}${safeManning != null ? ` / ${safeManning}` : ''}`}
              />
            </ReportMetaRow>
          </ReportMeta>
        </ReportHeader>

        <ReportBody>
          <table className="crew-list-print-table">
            <thead>
              <tr>
                <th className="crew-list-print-col-no">No.</th>
                <th>Rank</th>
                <th>Name</th>
                <th>Nationality</th>
                <th className="crew-list-print-col-num">Age</th>
                <th>Sign-on</th>
                <th className="crew-list-print-col-num">Months</th>
                <th>COE</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="crew-list-print-empty">
                    No crew assigned for this vessel and period.
                  </td>
                </tr>
              ) : (
                rows.map((row, i) => (
                  <tr key={row.transactionId}>
                    <td className="crew-list-print-col-no">{i + 1}</td>
                    <td>{dash(row.lastRank)}</td>
                    <td>{fullName(row)}</td>
                    <td>{dash(row.nationality)}</td>
                    <td className="crew-list-print-col-num">{row.age ?? '—'}</td>
                    <td>{fmtDate(row.signOnDate)}</td>
                    <td className="crew-list-print-col-num">{row.monthsOnboard ?? '—'}</td>
                    <td>{fmtDate(row.coeDate ?? row.latest ?? row.extCoeDate)}</td>
                    <td>{statusLabel(row.status)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </ReportBody>

        <ReportFooter>
          <ReportSigns>
            <ReportSignLine label="Master" />
            <ReportSignLine label="Date" />
          </ReportSigns>
          {companyName ? <ReportNote>Issued by {companyName}</ReportNote> : null}
        </ReportFooter>
      </ReportPage>
    </ReportPack>
  );
};

export default OnboardCrewListReport;
